import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { fecha } from '../utils/formato';

// El panel del dueño del programa: quién se registró y quién puede entrar.
//
// Solo lo ve la primera cuenta que se creó. La seguridad de verdad está en el
// servidor —`soloAdmin`—; esconder la pestaña es únicamente para no enseñar un
// botón que va a dar «no autorizado».
//
// La lista viene ordenada con las PENDIENTES primero. Ordenada por fecha, la
// que hay que activar queda enterrada entre las que ya están funcionando, y
// entonces alguien que acaba de pagar espera sin motivo.

const PINTA = {
  pendiente:  ['ojo',   '⏳ Esperando activación'],
  activa:     ['bueno', '✅ Activa'],
  suspendida: ['malo',  '⛔ Suspendida'],
};

function Empresas() {
  const [empresas, setEmpresas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [aviso, setAviso] = useState(null);
  const [meses, setMeses] = useState({});

  const cargar = useCallback(() => {
    setCargando(true);
    axios.get('/api/admin/empresas')
      .then(r => setEmpresas(r.data || []))
      .catch(() => setAviso({ tipo: 'malo', texto: 'No se pudieron cargar las empresas' }))
      .finally(() => setCargando(false));
  }, []);

  useEffect(cargar, [cargar]);

  const activar = async (e, cuanto) => {
    try {
      const { data } = await axios.post(`/api/admin/empresas/${e.id}/activar`, cuanto);
      setAviso({ tipo: 'bueno',
        texto: data.prueba
          ? `${e.nombre} tiene prueba hasta el ${fecha(data.activa_hasta)}. ` +
            'Cuando pague, póngale los meses y deja de ser prueba.'
          : `${e.nombre} queda activa hasta el ${fecha(data.activa_hasta)}.` });
      cargar();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo activar' });
    }
  };

  const suspender = async (e) => {
    if (!window.confirm(
      `¿Suspender a ${e.nombre}?\n\n` +
      'No va a poder entrar hasta que la vuelva a activar. NO se borra nada: ' +
      'sus datos quedan esperándola.'
    )) return;
    try {
      await axios.post(`/api/admin/empresas/${e.id}/suspender`);
      cargar();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo suspender' });
    }
  };

  const pendientes = empresas.filter(e => e.estado === 'pendiente').length;

  // Cuántos días le quedan. Negativo = ya venció.
  const diasQueQuedan = (hasta) => {
    if (!hasta) return null;
    const d = Math.round(
      (new Date(String(hasta).slice(0, 10) + 'T00:00:00') - new Date(new Date().toDateString()))
      / 86400000);
    return d;
  };

  return (
    <>
      <div className="encabezado">
        <h1>Empresas</h1>
        <p>Quién se registró en el programa y quién puede entrar</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      {pendientes > 0 && (
        <div className="aviso ojo">
          <strong>
            {pendientes === 1
              ? 'Hay 1 empresa esperando que la active'
              : `Hay ${pendientes} empresas esperando que las active`}
          </strong>
          {' '}— registradas pero sin poder entrar todavía.
        </div>
      )}

      <div className="bloque">
        <h2>
          🏢 Registradas
          <span className="contador">{empresas.length}</span>
        </h2>

        {cargando ? (
          <div className="vacio">Cargando…</div>
        ) : empresas.length === 0 ? (
          <div className="vacio">Todavía no se ha registrado nadie.</div>
        ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Estado</th>
                  <th>Hasta cuándo</th>
                  <th style={{ textAlign: 'right' }}>Uso</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {empresas.map(e => {
                  const [pinta, texto] = PINTA[e.estado] || PINTA.pendiente;
                  const dias = diasQueQuedan(e.activa_hasta);
                  return (
                    <tr key={e.id}>
                      <td className="recio">
                        {e.nombre}
                        <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                          {e.email}{e.dueno ? ` · ${e.dueno}` : ''}
                        </div>
                      </td>
                      <td>
                        {/* La prueba se distingue de lo pagado a simple vista.
                            Con solo la fecha, a los diez días él no sabría si
                            llamar a preguntar qué le pareció o a cobrar. */}
                        <span className={'pinta ' + (e.prueba && e.estado === 'activa' ? 'ojo' : pinta)}>
                          {e.prueba && e.estado === 'activa' ? '🎁 En prueba' : texto}
                        </span>
                      </td>
                      <td>
                        {e.activa_hasta ? (
                          <>
                            {fecha(e.activa_hasta)}
                            {/* Los días que quedan, que es lo que decide si hay
                                que llamar. Una fecha sola obliga a restar. */}
                            {dias !== null && dias <= 30 && e.estado === 'activa' && (
                              <div style={{ fontSize: 12, fontWeight: 700,
                                            color: dias < 0 ? 'var(--malo)' : 'var(--ojo)' }}>
                                {dias < 0 ? `venció hace ${-dias} días`
                                  : dias === 0 ? 'vence hoy'
                                  : `le quedan ${dias} días`}
                              </div>
                            )}
                          </>
                        ) : <span style={{ color: 'var(--tinta-tenue)' }}>—</span>}
                      </td>
                      <td className="num" style={{ fontSize: 13 }}>
                        {e.recibos > 0 || e.cuajadas > 0 ? (
                          <>
                            {e.recibos} recibos · {e.cuajadas} cuajadas
                            {e.ultima_leche && (
                              <div style={{ color: 'var(--tinta-tenue)', fontSize: 12 }}>
                                última leche {fecha(e.ultima_leche)}
                              </div>
                            )}
                          </>
                        ) : <span style={{ color: 'var(--tinta-tenue)' }}>sin usar</span>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {/* La prueba va primero y aparte: es lo que se da al
                            principio, antes de que haya plata de por medio.

                            Se ofrece a quien TODAVÍA NO HA PAGADO: la que está
                            esperando activación, la suspendida, y también la que
                            ya está en prueba —para poder alargarla cuando el
                            cliente pide unos días más, que es lo normal—.

                            Al que ya pagó no se le ofrece: darle días de prueba
                            lo marcaría como prueba y dejaría de verse como
                            cliente. */}
                        {(e.prueba || e.estado !== 'activa') && (
                          <>
                            <button className="boton chico suave"
                                    onClick={() => activar(e, { dias: 10 })}>
                              🎁 10 días de prueba
                            </button>{' '}
                          </>
                        )}
                        <select value={meses[e.id] || '12'} style={{ width: 90, marginRight: 5 }}
                                onChange={ev => setMeses({ ...meses, [e.id]: ev.target.value })}>
                          <option value="1">1 mes</option>
                          <option value="3">3 meses</option>
                          <option value="6">6 meses</option>
                          <option value="12">1 año</option>
                        </select>
                        <button className="boton chico"
                                onClick={() => activar(e, { meses: parseInt(meses[e.id]) || 12 })}>
                          {e.prueba ? 'Ya pagó' : e.estado === 'activa' ? 'Renovar' : 'Activar'}
                        </button>{' '}
                        {e.estado !== 'suspendida' && !e.propia && (
                          <button className="boton chico suave" onClick={() => suspender(e)}>
                            Suspender
                          </button>
                        )}
                        {e.propia && (
                          <span style={{ color: 'var(--tinta-tenue)', fontSize: 13 }}>
                            su empresa
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="aviso" style={{ marginTop: 12, marginBottom: 0 }}>
          <strong>🎁 10 días de prueba</strong> para que se familiarice, y cuando
          le pague, <strong>«Ya pagó»</strong> con los meses — ahí deja de ser
          prueba y pasa a ser cliente.
          <div style={{ marginTop: 6 }}>
            Los meses se suman <strong>a lo que ya le quedaba</strong>, no desde
            hoy: a quien todavía tiene días no se le quitan. Y suspender no borra
            nada — los datos quedan esperando.
          </div>
        </div>
      </div>
    </>
  );
}

export default Empresas;
